import type { Candidate, CheckText, RiskText, RuleGroupView, RulesView } from '@tabel/shared/schemas/rules';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, Info, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useToast } from '../../components/ErrorToast';
import { useUnsavedChanges } from '../../components/UnsavedGuard';
import { Button, Callout } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError } from '../../lib/errors';
import { AnswerEdit, DetailsEdit, type EditModel } from './AnswerText';
import { EditExample, type OpenDay } from './ExampleDay';
import { IMPACT_DELAY_MS, type ImpactInput, ImpactLines, useImpact } from './ImpactLines';
import { type FieldIndex, type Values, sameValue } from './answer';
import {
  changedKeys,
  editValues,
  initialValues,
  inputScale,
  keyOf,
  parseScaled,
  rawOf,
  shownValues,
  typed,
} from './edit';
import { DRAFT, LABEL, T, say } from './texts';

/*
 * Правило в изменении (004, T063–T065; contracts/ui.md, «Карточка правила» → «Изменение»; FR-019–FR-026, FR-031):
 * - ответ тем же шаблоном, значения — поля внутри предложений; поля под ответом;
 * - ошибка у поля — красным (что не так и что допустимо), предупреждение — жёлтым (FR-024, FR-025): проверку формы
 *   поля делает интерфейс, проверку набора — сервер (ошибки и риски черновика, влияния);
 * - пример «Сейчас / После публикации», строки «Это изменение» и «Весь черновик»;
 * - подвал «Отменить» · «Сохранить в черновик»; почему нельзя сохранить — рядом с кнопкой; несохранённое — вид
 *   `rule` в `UnsavedGuard`.
 * Запись поверх изменённого черновика сервер отклоняет (`DRAFT_CHANGED`): введённое остаётся, страница перечитывает
 * черновик, человек сохраняет снова.
 */

/** Значение через `ms` после последнего изменения. */
function useDebounced<T>(value: T, key: string, ms: number): T {
  const [out, setOut] = useState(value);
  const latest = useRef(value);
  latest.current = value;
  // biome-ignore lint/correctness/useExhaustiveDependencies: ждём по ключу значения
  useEffect(() => {
    const t = setTimeout(() => setOut(latest.current), ms);
    return () => clearTimeout(t);
  }, [key, ms]);
  return out;
}

/**
 * Ошибки и риски кандидата из ответа влияния: в ответе или в «Это изменение» (поток влияния считает `errors`
 * кандидата, contracts/impact.md). В схеме `ImpactResponse` их пока нет — берутся, только если у них есть текст.
 */
function checksOf(res: unknown): { errors: CheckText[]; risks: RiskText[] } {
  type Part = { errors?: unknown; risks?: unknown } | null | undefined;
  const r = res as (Part & { thisChange?: Part }) | null;
  const list = (...xs: unknown[]) =>
    xs.flatMap((x) => (Array.isArray(x) ? x : [])).filter((x) => typeof x?.text === 'string');
  return {
    errors: list(r?.errors, r?.thisChange?.errors) as CheckText[],
    risks: list(r?.risks, r?.thisChange?.risks) as RiskText[],
  };
}

const errKey = (e: CheckText) => (e.cube && e.field ? `${e.cube}.${e.field}` : null);

export function RuleEditor({
  group,
  view,
  fields,
  byId,
  date,
  today,
  note,
  tzOffset,
  onClose,
  onOpenDay,
  seed,
}: {
  /** Правило в той версии, от которой строится изменение (черновик или последняя опубликованная). */
  group: RuleGroupView;
  /** Правила той же версии: блоки кубика, который делят два правила, и черновик. */
  view: RulesView;
  fields: FieldIndex;
  byId: ReadonlyMap<string, RuleGroupView>;
  /** Дата правил на странице: пример правила. */
  date: string;
  today: string;
  /** «Изменение строится от правил, которые начнут действовать …». */
  note: string | null;
  tzOffset: string;
  onClose: () => void;
  onOpenDay: (day: OpenDay) => void;
  /**
   * Значения, с которыми изменение открывается поверх версии: «Добавить» из каталога включает правило первым
   * значением, кроме «Не применять» (US6, FR-055). Это уже изменение: пример и влияние считаются сразу.
   */
  seed?: Values;
}) {
  const ids = useId();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const [initial] = useState<Values>(() => initialValues(group));
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...initial, ...seed }));
  // Единица ввода решается один раз при открытии: поле в секундах — в минутах, если значение делится на 60.
  const [scales] = useState<Readonly<Record<string, number>>>(() =>
    Object.fromEntries(group.fields.filter(typed).map((f) => [keyOf(f), inputScale(f, initial[keyOf(f)])])),
  );
  const scaleOf = (key: string) => scales[key] ?? 1;
  const [raw, setRaw] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      group.fields
        .filter(typed)
        .map((f) => [
          keyOf(f),
          rawOf(f, (seed ?? initial)[keyOf(f)] ?? initial[keyOf(f)], scaleOf(keyOf(f))),
        ]),
    ),
  );
  const [local, setLocal] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);

  const draft = view.draft ?? null;
  // Скрытое при этих значениях поле — не правка (`shownValues`).
  const changed = changedKeys(shownValues(group, initial, values), shownValues(group, values)).length > 0;
  const localKeys = Object.keys(local);
  const edit = useMemo(() => editValues(group, values, fields), [group, values, fields]);
  const candidate: Candidate = { base: draft ? 'draft' : 'version', group: group.id, values: edit };

  // Пока значение не исправлено, пример и влияние — по последним верным значениям.
  const valid = useRef(candidate);
  if (localKeys.length === 0) valid.current = candidate;
  const exampleCandidate = useDebounced(valid.current, JSON.stringify(valid.current), IMPACT_DELAY_MS);
  const impactReq: ImpactInput | null =
    changed || group.draft?.changed
      ? {
          ...valid.current,
          ...(draft ? { draftEtag: draft.etag, validFrom: draft.defaultValidFrom } : {}),
        }
      : null;
  const impact = useImpact(impactReq);

  const server = impact.status === 'done' ? checksOf(impact.result) : { errors: [], risks: [] };
  // Ошибки проверки последних введённых значений (ответ влияния) — всегда; ошибки сохранённого черновика у поля —
  // пока значение поля то же, с которым их прислали.
  const saved = (group.draft?.errors ?? []).filter((e) => {
    const k = errKey(e);
    return k === null || sameValue(values[k], initial[k]);
  });
  const errors = [...server.errors, ...saved].filter(
    (e, i, all) => all.findIndex((x) => x.code === e.code && errKey(x) === errKey(e)) === i,
  );
  const risks = [...server.risks, ...(group.draft?.risks ?? [])].filter(
    (r, i, all) => all.findIndex((x) => x.code === r.code) === i,
  );

  const model: EditModel = {
    group,
    fields,
    values,
    base: group.values,
    raw,
    scale: scaleOf,
    invalid: (key) => local[key] !== undefined || errors.some((e) => errKey(e) === key),
    describedBy: (key) => {
      const own = [
        local[key] !== undefined ? `${ids}-local-${key}` : '',
        ...errors.filter((e) => errKey(e) === key).map((e) => `${ids}-err-${e.code}-${key}`),
      ].filter(Boolean);
      return own.length > 0 ? own.join(' ') : undefined;
    },
    set: (key, value) => setValues((v) => ({ ...v, [key]: value })),
    type: (key, text) => {
      setRaw((r) => ({ ...r, [key]: text }));
      const f = fields.get(key);
      if (!f) return;
      const p = parseScaled(f, text, scaleOf(key));
      if ('error' in p) {
        setLocal((l) => ({ ...l, [key]: p.error }));
        return;
      }
      setLocal((l) => Object.fromEntries(Object.entries(l).filter(([k]) => k !== key)));
      setValues((v) => ({ ...v, [key]: p.value }));
    },
  };

  const reason = saving
    ? null
    : localKeys.length > 0
      ? say.fixField(fields.get(localKeys[0] as string)?.label ?? '')
      : !changed
        ? T.noChange
        : null;

  const save = async () => {
    setSaving(true);
    try {
      await api.saveBlocks({ ...(draft ? { etag: draft.etag } : {}), group: group.id, values: edit });
    } catch (e) {
      setSaving(false);
      if (e instanceof ApiError && (e.code === 'DRAFT_CHANGED' || e.code === 'DRAFT_LED_BY_OTHER'))
        void qc.invalidateQueries({ queryKey: rulesKeys.all });
      if (e instanceof ApiError && e.code === 'DRAFT_CHANGED') setConflict(true);
      else showError(e, { screen: 'Rules', action: 'save draft', retry: () => void save().catch(() => {}) });
      throw e;
    }
    setSaving(false);
    showInfo(view.launch ? T.savedToLaunchDraft : T.savedToDraft, { duration: 6000 });
    await qc.invalidateQueries({ queryKey: rulesKeys.all });
    onClose();
  };

  useUnsavedChanges(
    changed
      ? {
          kind: 'rule',
          summary: say.ruleSummary(group.question),
          canSave: localKeys.length === 0 && !saving,
          save,
          discard: onClose,
        }
      : null,
  );

  return (
    <div className="flex flex-col gap-3">
      {note && (
        <Callout tone="accent" icon={Info}>
          {note}
        </Callout>
      )}
      {conflict && (
        <Callout tone="warn" icon={TriangleAlert}>
          {T.conflict}
        </Callout>
      )}
      <AnswerEdit model={model} />
      {group.hiddenOptions > 0 && (
        <p className="text-[12.5px] text-ink-3">{say.hiddenOptions(group.hiddenOptions)}</p>
      )}
      <DetailsEdit model={model} />
      {(localKeys.length > 0 || errors.length > 0 || risks.length > 0) && (
        <div className="space-y-1.5">
          {localKeys.map((k) => (
            <p
              key={`local-${k}`}
              id={`${ids}-local-${k}`}
              data-tone="bad"
              className="flex items-start gap-2 rounded-[8px] bg-bad-soft px-2.5 py-2 text-[13px] leading-[1.45] text-bad"
            >
              <CircleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
              {local[k]}
            </p>
          ))}
          <ChecksList
            errors={errors}
            risks={risks}
            byId={byId}
            idOf={(e) => `${ids}-err-${e.code}-${errKey(e) ?? ''}`}
          />
        </div>
      )}
      {/* Правила к запуску (007, FR-004a): дней площадки ещё нет — примера «сейчас → после» нет. */}
      {group.hasExample && !view.launch && (
        <EditExample
          group={group.id}
          date={date}
          today={today}
          candidate={exampleCandidate}
          impact={impact}
          tzOffset={tzOffset}
          onOpen={onOpenDay}
        />
      )}
      {view.launch ? (
        // Правила к запуску (007, FR-004a): дни площадки до запуска не считаются, проверка значений — как обычно.
        <p className="text-[12.5px] text-ink-2">{T.launchNoDays}</p>
      ) : (
        <ImpactLines
          state={impact}
          showWhole={draft?.changes.some((c) => c.group !== group.id) === true}
          effect={group.effect}
        />
      )}
      <div className="flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:flex-auto">
        {reason ? (
          <p className="mr-auto text-[12.5px] text-ink-2 max-sm:w-full">{reason}</p>
        ) : (
          errors.length > 0 && (
            <p className="mr-auto text-[12.5px] text-warn max-sm:w-full">{T.saveWithErrors}</p>
          )
        )}
        <Button onClick={onClose}>{DRAFT.cancelEdit}</Button>
        <Button
          variant="primary"
          icon={Check}
          disabled={reason !== null || saving}
          onClick={() => void save().catch(() => {})}
        >
          {LABEL.saveDraft}
        </Button>
      </div>
    </div>
  );
}

/** Ошибки (красным) и предупреждения (жёлтым) черновика: текст, пример, связанные правила. */
export function ChecksList({
  errors,
  risks,
  byId,
  idOf,
  onRule,
}: {
  errors: readonly CheckText[];
  risks: readonly RiskText[];
  byId: ReadonlyMap<string, RuleGroupView>;
  idOf?: (e: CheckText) => string;
  /** Ссылка на связанное правило (вид «Что изменится» переходит на «Правила»). */
  onRule?: () => void;
}) {
  return (
    <>
      {errors.map((e) => (
        <p
          key={`${e.code}|${errKey(e) ?? ''}`}
          id={idOf?.(e)}
          data-tone="bad"
          className="flex items-start gap-2 rounded-[8px] bg-bad-soft px-2.5 py-2 text-[13px] leading-[1.45] text-bad"
        >
          <CircleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
          {e.text}
        </p>
      ))}
      {risks.map((r) => {
        const related = r.related.flatMap((id) => byId.get(id) ?? []);
        return (
          <div
            key={r.code}
            data-tone="warn"
            className="flex items-start gap-2 rounded-[8px] bg-warn-soft px-2.5 py-2 text-[13px] leading-[1.45] text-warn"
          >
            <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
            <div className="min-w-0 space-y-0.5">
              <p>{r.text}</p>
              {r.example && <p className="text-ink-2">{`${LABEL.riskExample} ${r.example}`}</p>}
              {related.length > 0 && (
                <p className="text-ink-2">
                  {LABEL.related}{' '}
                  {related.map((g, i) => (
                    <span key={g.id}>
                      {i > 0 && ', '}«
                      <a
                        href={`#${g.id}`}
                        onClick={onRule}
                        className="underline-offset-2 hover:text-accent hover:underline"
                      >
                        {g.question}
                      </a>
                      »
                    </span>
                  ))}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
